import React, { useContext, useEffect, useState } from 'react';
import _ from 'lodash';

import JobListContext from '../../context/jobListContext';
import AppStateContext from '../../context/appStateContext';
import AssetListContext from '../../context/assetListContext'

import PlannedStatusBubble from '../../../components/plannedStatusBubble';
import LocationLabel from '../../../components/locationLabel';
import DateLabel from '../../../components/dateLabel';
import ExportButton from "../../../components/exportButton";
import GetAssetsListFunction from '../../../functions/GetAssetsListFunction';
import GetAssetAttributes from "../../../functions/GetAssetAttributes";
import JobDetailAssetCardList from '../Lists/jobDetailAssetCardList';
import MaintenanceTypeBubble from '../../../components/maintenanceTypeBubble';
import useTextCorrection from '../../../hooks/useTextCorrection';
import DetailsTable from "../../../components/detailsTable";
import APIHelper from "../../../hooks/useAPIHelper";
import {tableData} from "../../../hooks/useFilterHelper";
import ApiStatus,{POPULATED,CALLING,EMPTY,TIMEOUT} from "../../../components/apiStatus";

import {
  BubbleTitle,
  BubbleSection,
  ContentCtn,
  HeadingDescCtn,
  HeadingDesc,
  JobDetailCtn,
  JobTitle,
  SectionTitle,
  RoutineDescriptionCtn,
  AssetDetailList,
  ButtonCtn,
} from './index.style';

const JobDetailPlanned: React.FC<any> = (): React.ReactElement => {
  const { selectedPlannedJob, clearSelectedPlannedJob, setAppJobDetails, updatePlannedAttributes } = useContext(JobListContext);
  const { token, selectedCustomerId } = useContext(AppStateContext);
  const { clearAssetDetails } = useContext(AssetListContext);

  const [renderedAsset, setRenderedAsset] = useState([]);
  const [currentApiStatus, setCurrentApiStatus] = useState(CALLING);

  const getAsset = APIHelper();
  const tableHeadings = tableData['planned'];

  // PORTAL-63. This previously branched on "typeof data == 'object'", which is true
  // for ARRAYS as well as objects - so the else was unreachable and a multi-unit
  // response rendered [[a, b]] with Make undefined. It only ever looked correct
  // because the SerialNo filter above normally matches exactly one unit, which the
  // parser then returned as a bare object. GetAssetsListFunction now always returns
  // an array, so there is one shape and no branch.
  const assetCallback = (data: []| string) => {
    if (data == 'timeout') {
      setCurrentApiStatus(TIMEOUT);
      return;
    }

    if (!data || data.length == 0) {
      setCurrentApiStatus(EMPTY);
      return;
    }

    setCurrentApiStatus(POPULATED);
    updateMake(data[0].Make);
    setRenderedAsset(data);
  };

  const updateMake = (makeName: string) => {
    let oldPlannedJob = _.cloneDeep(selectedPlannedJob);

    setAppJobDetails({
      selectedPlannedJob: _.assign(oldPlannedJob,{
        Make: makeName
      })
    })
  };

  useEffect(() => {
    return () => {
      clearAssetDetails();
      clearSelectedPlannedJob();
    }
  },[]);

  useEffect(() => {

    if(selectedPlannedJob.PMCallNo == '' || selectedPlannedJob.PMCallNo == 0) {
      setCurrentApiStatus(EMPTY);
      return;
    }

    if(selectedPlannedJob.SerialNo != "") {
      setCurrentApiStatus(CALLING);
      // PORTAL-72. The CustomerCode is here so this call can be answered from the published
      // per-customer Assets document; without it the lambda has no way to know WHICH customer's
      // file to look in, and this panel was the one asset surface that could not be cached.
      // It does NOT narrow the Pronto query - that endpoint returns zero rows for both filters
      // together, so the lambda drops the serial before forwarding and applies it itself.
      let filter: string = `<Filters><CustomerCode><Like>${selectedCustomerId}</Like></CustomerCode><SerialNo><Like>${selectedPlannedJob.SerialNo}</Like></SerialNo></Filters>`;
      getAsset.run(GetAssetsListFunction,assetCallback,[filter, token, selectedPlannedJob.ContractNo]).then(() => {
        getAsset.run(GetAssetAttributes, (data: []) => updatePlannedAttributes(data), [token, selectedPlannedJob.SerialNo]);
      });
    }
  },[selectedPlannedJob.PMCallNo]);

  const { filterText, replaceBreaks, titleCase } = useTextCorrection();

  return (
    <JobDetailCtn>
      <ContentCtn>
        <JobTitle>{titleCase(filterText(selectedPlannedJob.ItemDescription))}</JobTitle>
        <HeadingDescCtn>
          <HeadingDesc>
            <LocationLabel location={selectedPlannedJob.UnitLocation} />
          </HeadingDesc>
          <HeadingDesc>
            <DateLabel
              slaStatus={selectedPlannedJob.StatusCode == 'F' ? 'doneOnTime' : 'upcoming'}
              date={selectedPlannedJob.RoutineDueDate}
              page="planned"
            />
          </HeadingDesc>
        </HeadingDescCtn>
        <BubbleSection>
          <div>
            <BubbleTitle>Status</BubbleTitle>
            <PlannedStatusBubble status={selectedPlannedJob.Status} />
          </div>
          <div>
            <BubbleTitle>Maintenance type</BubbleTitle>
            <MaintenanceTypeBubble type={selectedPlannedJob.RoutineCode} />
          </div>
        </BubbleSection>
        <RoutineDescriptionCtn>
          <SectionTitle>Routine procedure tasks</SectionTitle>
          <div>{replaceBreaks(selectedPlannedJob.RoutineProcedureTaskText)}</div>
        </RoutineDescriptionCtn>
        <AssetDetailList>
          <SectionTitle>Asset</SectionTitle>
          <ApiStatus status={currentApiStatus} type="bar" >
            <JobDetailAssetCardList assetList={renderedAsset} location="planned" />
          </ApiStatus>
        </AssetDetailList>
        <div>
          <SectionTitle>Maintenance details</SectionTitle>
          <DetailsTable headings={tableHeadings} tableData={selectedPlannedJob}/>
        </div>
      </ContentCtn>
      <ButtonCtn>
        <ExportButton data={selectedPlannedJob} columns={Object.values(tableHeadings)}/>
      </ButtonCtn>
    </JobDetailCtn>
  );
};

export default JobDetailPlanned;
